import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import type { SampleVersionResponse, FieldChangeDto, ExtraFieldChangeDto } from '../api/types'

interface VersionHistoryProps {
  sampleId: string
  sampleTitle?: string
  onClose: () => void
}

export function VersionHistory({ sampleId, sampleTitle, onClose }: VersionHistoryProps) {
  const { apiClient } = useAuth()
  const [versions, setVersions] = useState<SampleVersionResponse[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set())
  const [fieldCategories, setFieldCategories] = useState<Record<string, string>>({})

  const loadData = useCallback(async () => {
    if (!apiClient) return

    try {
      setLoading(true)
      setError(null)
      const [versionsRes, categoriesRes] = await Promise.all([
        apiClient.getSampleVersions(sampleId, page, 20),
        apiClient.getFieldCategories(),
      ])
      setVersions(versionsRes.versions)
      setTotalPages(versionsRes.totalPages)
      setTotalCount(versionsRes.totalCount)
      setFieldCategories(categoriesRes)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to load version history')
      }
    } finally {
      setLoading(false)
    }
  }, [apiClient, sampleId, page])

  useEffect(() => {
    loadData()
  }, [loadData])

  const toggleExpand = (versionId: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev)
      if (next.has(versionId)) {
        next.delete(versionId)
      } else {
        next.add(versionId)
      }
      return next
    })
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'object') return JSON.stringify(value, null, 2)
    return String(value)
  }

  const getStatusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      pending: { bg: 'var(--color-warning)', text: '#000' },
      accepted: { bg: 'var(--color-success)', text: '#fff' },
      rejected: { bg: 'var(--color-danger)', text: '#fff' },
      'auto-applied': { bg: 'var(--color-info, #3b82f6)', text: '#fff' },
    }
    const color = colors[status] || { bg: 'var(--color-text-muted)', text: '#fff' }

    return (
      <span
        className="badge"
        style={{
          backgroundColor: color.bg,
          color: color.text,
        }}
      >
        {status}
      </span>
    )
  }

  const getSourceBadge = (sourceType: string, sourceVendor?: string | null, sourceUserEmail?: string | null) => {
    if (sourceType === 'vendor_email') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            border: '1px solid var(--color-border)',
          }}
        >
          Vendor: {sourceVendor || 'Unknown'}
        </span>
      )
    }
    if (sourceType === 'app_user') {
      return (
        <span
          className="badge"
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            border: '1px solid var(--color-border)',
          }}
        >
          User: {sourceUserEmail || 'Unknown'}
        </span>
      )
    }
    return (
      <span className="badge" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
        {sourceType}
      </span>
    )
  }

  const getCategoryBadge = (fieldName: string) => {
    const category = fieldCategories[fieldName]
    if (!category) return null

    const colors: Record<string, { bg: string; text: string }> = {
      vendor_authoritative: { bg: 'var(--color-info, #3b82f6)', text: '#fff' },
      team_authoritative: { bg: 'var(--color-warning)', text: '#000' },
      additive: { bg: 'var(--color-success)', text: '#fff' },
      standard: { bg: 'var(--color-text-muted)', text: '#fff' },
    }
    const color = colors[category] || colors.standard

    return (
      <span
        className="badge"
        style={{
          backgroundColor: color.bg,
          color: color.text,
          fontSize: 'var(--font-size-xs)',
          marginLeft: 'var(--spacing-xs)',
        }}
      >
        {category.replace('_', ' ')}
      </span>
    )
  }

  const renderChanges = (changes: Record<string, FieldChangeDto>) => {
    const entries = Object.entries(changes)
    if (entries.length === 0) {
      return <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No field changes recorded</p>
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {entries.map(([field, change]) => (
          <div
            key={field}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'var(--color-bg-primary)',
              borderRadius: 'var(--radius-sm)',
              borderLeft: '3px solid var(--color-primary)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-xs)' }}>
              <span style={{ fontWeight: 600, fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
                {field}
              </span>
              {getCategoryBadge(field)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 'var(--spacing-sm)', alignItems: 'start' }}>
              <div style={{
                padding: 'var(--spacing-xs) var(--spacing-sm)',
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                maxHeight: '100px',
                overflow: 'auto',
              }}>
                {formatValue(change.old)}
              </div>
              <span style={{ color: 'var(--color-text-muted)', paddingTop: 'var(--spacing-xs)' }}>→</span>
              <div style={{
                padding: 'var(--spacing-xs) var(--spacing-sm)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                maxHeight: '100px',
                overflow: 'auto',
              }}>
                {formatValue(change.new)}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const getResolutionBadge = (resolution: string | null, mappedTo: string | null) => {
    if (!resolution) {
      return (
        <span className="badge" style={{ backgroundColor: 'var(--color-text-muted)', color: '#fff' }}>
          pending
        </span>
      )
    }

    const colors: Record<string, { bg: string; text: string }> = {
      keep: { bg: 'var(--color-success)', text: '#fff' },
      discard: { bg: 'var(--color-danger)', text: '#fff' },
      map: { bg: 'var(--color-info, #3b82f6)', text: '#fff' },
    }
    const color = colors[resolution] || { bg: 'var(--color-text-muted)', text: '#fff' }

    let label = resolution
    if (resolution === 'map' && mappedTo) {
      label = `mapped → ${mappedTo}`
    } else if (resolution === 'keep') {
      label = 'kept in extra_data'
    } else if (resolution === 'discard') {
      label = 'discarded'
    }

    return (
      <span className="badge" style={{ backgroundColor: color.bg, color: color.text }}>
        {label}
      </span>
    )
  }

  const renderExtraFieldChanges = (extraFields: Record<string, ExtraFieldChangeDto> | null) => {
    if (!extraFields) return null
    const entries = Object.entries(extraFields)
    if (entries.length === 0) return null

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {entries.map(([vendorKey, field]) => (
          <div
            key={vendorKey}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'var(--color-bg-primary)',
              borderRadius: 'var(--radius-sm)',
              borderLeft: '3px solid var(--color-warning)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-xs)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                <span style={{ fontWeight: 600, fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
                  {vendorKey}
                </span>
                <span className="badge" style={{ backgroundColor: 'var(--color-warning)', color: '#000', fontSize: 'var(--font-size-xs)' }}>
                  extra field
                </span>
              </div>
              {getResolutionBadge(field.resolution, field.mappedTo)}
            </div>
            <div style={{
              padding: 'var(--spacing-xs) var(--spacing-sm)',
              backgroundColor: 'rgba(249, 115, 22, 0.1)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              wordBreak: 'break-word',
            }}>
              {field.value}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-wide"
        style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Version History</h3>
          {sampleTitle && (
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-md)' }}>
              {sampleTitle}
            </span>
          )}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-md)' }}>
          {error && (
            <div style={{
              padding: 'var(--spacing-md)',
              backgroundColor: 'var(--color-danger-bg, rgba(220, 38, 38, 0.1))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-md)',
              color: 'var(--color-danger)',
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>
              Loading version history...
            </div>
          ) : versions.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 'var(--spacing-xl)',
              color: 'var(--color-text-secondary)',
            }}>
              No version history found for this sample.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                Showing {versions.length} of {totalCount} versions
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                {versions.map((version) => {
                  const isExpanded = expandedVersions.has(version.versionId)
                  const changeCount = Object.keys(version.changes).length

                  const extraFieldCount = version.extraFieldChanges ? Object.keys(version.extraFieldChanges).length : 0

                  return (
                    <div
                      key={version.versionId}
                      style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--color-bg-secondary)',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Version Header */}
                      <div
                        style={{
                          padding: 'var(--spacing-sm) var(--spacing-md)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          backgroundColor: isExpanded ? 'var(--color-bg-tertiary)' : 'transparent',
                        }}
                        onClick={() => toggleExpand(version.versionId)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                            v{version.versionNumber}
                          </span>
                          {getStatusBadge(version.status)}
                          {getSourceBadge(version.sourceType, version.sourceVendor, version.sourceUserEmail)}
                          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                            {changeCount} field{changeCount !== 1 ? 's' : ''} changed
                            {extraFieldCount > 0 && `, ${extraFieldCount} extra field${extraFieldCount !== 1 ? 's' : ''}`}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                            {new Date(version.createdAt).toLocaleString()}
                          </span>
                          <span style={{
                            color: 'var(--color-text-muted)',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 0.2s',
                          }}>
                            v
                          </span>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div style={{ padding: 'var(--spacing-md)', borderTop: '1px solid var(--color-border)' }}>
                          {/* Metadata */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: 'var(--spacing-sm)',
                            marginBottom: 'var(--spacing-md)',
                            padding: 'var(--spacing-sm)',
                            backgroundColor: 'var(--color-bg-primary)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-xs)',
                          }}>
                            {version.sourceIngestionId && (
                              <div>
                                <span style={{ color: 'var(--color-text-muted)' }}>Ingestion ID: </span>
                                <span className="mono">{version.sourceIngestionId.slice(0, 8)}...</span>
                              </div>
                            )}
                            {version.reviewedByEmail && (
                              <div>
                                <span style={{ color: 'var(--color-text-muted)' }}>Reviewed by: </span>
                                <span>{version.reviewedByEmail}</span>
                              </div>
                            )}
                            {version.reviewedAt && (
                              <div>
                                <span style={{ color: 'var(--color-text-muted)' }}>Reviewed at: </span>
                                <span>{new Date(version.reviewedAt).toLocaleString()}</span>
                              </div>
                            )}
                            {version.reviewNotes && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: 'var(--color-text-muted)' }}>Review notes: </span>
                                <span>{version.reviewNotes}</span>
                              </div>
                            )}
                          </div>

                          {/* Changes */}
                          <h4 style={{ margin: '0 0 var(--spacing-sm) 0', fontSize: 'var(--font-size-sm)' }}>Field Changes</h4>
                          {renderChanges(version.changes)}

                          {/* Extra Fields */}
                          {version.extraFieldChanges && extraFieldCount > 0 && (
                            <div style={{ marginTop: 'var(--spacing-lg)' }}>
                              <h4 style={{ margin: '0 0 var(--spacing-sm) 0', fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                Extra Vendor Fields
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                                  (unmapped fields from vendor submission)
                                </span>
                              </h4>
                              {renderExtraFieldChanges(version.extraFieldChanges)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 'var(--spacing-sm)',
                  marginTop: 'var(--spacing-md)',
                }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    Previous
                  </button>
                  <span style={{
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    color: 'var(--color-text-secondary)',
                    fontSize: 'var(--font-size-sm)',
                  }}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
